import React from 'react';
import { faCog, faProjectDiagram, faUsers } from '@fortawesome/pro-light-svg-icons';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ProfileType } from '@/types';
import { IState } from '@/state/IState';
import { useSelector } from 'react-redux';
import NavigationContextMenu from '../NavigationContextMenu';
import HeaderProfileThumbnail from './HeaderProfileThumbnail';
import { useImpersonationInfo } from '@/utils/hooks';
import { ProjectFeatureToggleFlags, useProjectHasFeatureToggle } from '@ygtechnologies/bygr-frontend-component-library';

interface HeaderProfileMenuProps {}

const HeaderProfileMenu: React.FC<HeaderProfileMenuProps> = (props) => {
    const impersonationInfo = useImpersonationInfo();

    const activeProfile = useSelector((state: IState) => state.global.activeProfile);
    const userProfiles = useSelector((state: IState) => state.global.profiles);
    const activeProfileInfo = userProfiles?.find((x) => x.profileId === activeProfile?.id);
    const showReleaseInformation = useProjectHasFeatureToggle(ProjectFeatureToggleFlags.ShowReleaseInformation);

    if (!userProfiles) {
        return null;
    }

    const isSeller = activeProfileInfo?.profileType === ProfileType.Seller;

    const otherUserProfiles = userProfiles.filter((p) => p.profileId !== activeProfileInfo?.profileId);

    const otherSellerUserProfiles = otherUserProfiles.filter((x) => x.profileType === ProfileType.Seller);
    const otherBuyerUsrProfiles = otherUserProfiles.filter((x) => x.profileType === ProfileType.Buyer);

    if (impersonationInfo) {
        return (
            <>
                {activeProfileInfo && (
                    <NavigationContextMenu.Item
                        imageElement={<HeaderProfileThumbnail profile={activeProfileInfo} sizeInPx={36} />}
                        title={activeProfileInfo.profileName}
                        subtitle={<Trans>Impersonating {impersonationInfo.unitName}</Trans>}
                    />
                )}

                <NavigationContextMenu.InternalLink
                    to="/impersonate/end"
                    icon={faCog}
                    title={<Trans>End impersonation</Trans>}
                />
            </>
        );
    }

    return (
        <>
            {activeProfileInfo && (
                <NavigationContextMenu.Item
                    imageElement={<HeaderProfileThumbnail profile={activeProfileInfo} sizeInPx={36} />}
                    title={activeProfileInfo.projectName ? activeProfileInfo.projectName : activeProfileInfo.profileName}
                    subtitle={activeProfileInfo.residentialUnitName}
                />
            )}

            {isSeller && activeProfile?.permissions.canChangeProfileSettings && (
                <NavigationContextMenu.InternalLink
                    to="/profile-settings"
                    icon={faCog}
                    title={<Trans>Company settings</Trans>}
                />
            )}

            {isSeller && activeProfile?.permissions.canManageUsers && (
                <NavigationContextMenu.InternalLink
                    to="/profile-users"
                    icon={faUsers}
                    title={<Trans>Users and permissions</Trans>}
                />
            )}

            {otherSellerUserProfiles.length > 0 && <NavigationContextMenu.Separator />}
            {otherSellerUserProfiles.map((p) => (
                <NavigationContextMenu.InternalLink
                    key={p.profileId}
                    imageElement={<HeaderProfileThumbnail profile={p} sizeInPx={36} />}
                    to={`/profiles/${p.profileId}`}
                    title={p.profileName}
                />
            ))}
            {showReleaseInformation && isSeller ? (
                <NavigationContextMenu.InternalLink
                    key="releases"
                    to="/releases"
                    icon={faProjectDiagram}
                    title={t`Releases for CM`}
                />
            ) : null}
            {otherBuyerUsrProfiles.length > 0 && <NavigationContextMenu.Separator />}
            {otherBuyerUsrProfiles.map((p) => (
                <NavigationContextMenu.InternalLink
                    key={p.profileId}
                    imageElement={<HeaderProfileThumbnail profile={p} sizeInPx={36} />}
                    to={`/profiles/${p.profileId}`}
                    title={p.projectName ? p.projectName : p.profileName}
                    subtitle={p.residentialUnitName}
                />
            ))}
        </>
    );
};

export default HeaderProfileMenu;
